// @ts-check
// Intérprete puro dos bots de conversa (contrato de bots, seção 7): recebe a definição, o estado da execução, um evento
// e o contexto que o executor montou, e devolve o estado novo, os efeitos (mensagens, chamadas às portas, integração,
// espera) e os passos (só o caminho e os nomes dos campos gravados, nunca o texto). Não conhece banco nem relógio: o
// Durable Object, o mock e o "Testar bot" do navegador rodam a mesma regra. Uma chamada anda até esperar resposta,
// esperar um efeito, terminar ou passar do limite de blocos por evento.
import {
  BOT_CAPTURE_ONLY_NODE_TYPES, findNextBotNodeId, hasDynamicOptions, hasStaticOptions, indexBotNodes, isWaitingNode, normalizeBotKeyword,
} from './bot-definition.js';
import {
  DEFAULT_BOT_RETRIES, MAXIMUM_BOT_NODES_PER_EVENT, MAXIMUM_BOT_STEPS_PER_RUN, MAXIMUM_BOT_SUMMARY_LENGTH, MAXIMUM_BOT_VARIABLE_VALUE_LENGTH,
  MAXIMUM_BOT_INTEGRATION_RESPONSE_BYTES, BOT_INTEGRATION_TIMEOUT_MILLISECONDS,
} from './bot-limits.js';
import { validateBotAnswer } from './bot-answer-validators.js';
import { renderBotText } from './bot-variables.js';
import { BOT_DEFAULT_TEXTS, BOT_IDENTITY_LEVEL_LABELS } from './bot-default-texts.js';
import { SUBJECT_MAX_LENGTH } from '../service-desk/service-limits.js';
import { WHATSAPP_INTERACTIVE_LIMITS, matchInteractiveAnswer, normalizeAnswerText, truncateTitle, encodeBotOptionId } from './whatsapp-interactive.js';
import { localParts, weekdayInTimeZone } from '../time/zoned-time.js';

/**
 * @typedef {import('./bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('./bot-definition.js').BotNode} BotNode
 * @typedef {import('./bot-definition.js').BotHandoffTarget} BotHandoffTarget
 * @typedef {import('./whatsapp-interactive.js').BotOutgoingMessage} BotOutgoingMessage
 * @typedef {{ id: string, title: string, value?: string | null, description?: string | null }} OfferedOption
 * @typedef {{ botId: string, revision: number | null, returnNodeId: string }} BotStackFrame
 * @typedef {{ title: string, value: string, isSensitive: boolean }} BotAnswerRecord
 * @typedef {{ id: string, botId: string, revision: number, activeBotId?: string, activeRevision?: number | null, currentNodeId: string | null,
 *   waitingFor: null | 'ANSWER' | 'EFFECT', waitingNodeId: string | null, attempts: number, stack: BotStackFrame[],
 *   variables: Record<string, string>, stepCount: number, splitSeed?: string | null, phase?: string | null, offered?: OfferedOption[],
 *   reminderSent?: boolean, timeoutAt?: string | null, pendingEffect?: { effectId: string, nodeId: string | null, purpose: string, data?: any } | null,
 *   effectSequence?: number, answers?: BotAnswerRecord[], sensitiveVariables?: string[], stepOutputs?: Record<string, any>,
 *   cardId?: string | null, ticket?: { id: string | null, protocol: string | null, subject: string | null } | null,
 *   team?: { id: string | null, name: string | null, nextOpeningAt: string | null } | null, visitDateTime?: string | null,
 *   visitTimeZone?: string | null, visitNextFrom?: string | null, slotTakenCount?: number,
 *   verification?: { codeId: string | null, destination: string | null, attempts: number } | null, trigger?: Record<string, any> | null,
 *   threadPurpose?: string | null, identityLevel?: string | null, speaker?: { personId: string, firstName: string | null, name: string | null } | null,
 *   students?: Array<{ personId: string, firstName: string | null, gradeLevelLabel: string | null }>, pendingWeak?: { cpf: string } | null,
 *   weakFailures?: number }} BotRunState
 * @typedef {{ kind: 'START', trigger?: Record<string, any> | null }
 *   | { kind: 'MESSAGE', text?: string | null, interactiveId?: string | null, attachments?: Array<{ fileId: string, kind: string }>,
 *       isGlobalWord?: 'MENU' | 'HUMAN' | null, isTextMessage?: boolean }
 *   | { kind: 'REMINDER' } | { kind: 'TIMEOUT' } | { kind: 'EFFECT_RESULT', effectId: string, result: any } | { kind: 'LEGAL_RESTRICTION' }} BotEvent
 *   LEGAL_RESTRICTION: o falante identificado tem restrição judicial; o bot para e transfere sem dizer o motivo (seção 12)
 * @typedef {{ threadId?: string | null, channelId?: string | null, threadPurpose?: string | null, familyStatus?: string | null,
 *   identityLevel?: string | null, speakerPersonId?: string | null, queueTeamId?: string | null, householdId?: string | null,
 *   teamOpen?: Record<string, { isOpen: boolean, nextOpeningAt: string | null }>, teamsById?: Record<string, { name: string, kind?: string }>,
 *   institutionName?: string | null, contact?: { firstName?: string | null, name?: string | null } | null, botName?: string | null,
 *   guardian?: { personId?: string | null, firstName?: string | null, name?: string | null } | null,
 *   card?: { id: string | null, stageLabel: string | null } | null, ticket?: { id: string | null, protocol: string | null, subject: string | null } | null,
 *   botsById?: Map<string, { name: string, purpose: string, revision: number, definition: BotDefinition }> | Record<string, any>,
 *   windowEndsAt?: string | null }} BotContext
 * @typedef {{ kind: 'SEND', message: BotOutgoingMessage, displayText: string, nodeId: string | null, botId: string }
 *   | { kind: 'PORT_CALL', effectId: string, port: 'serviceDesk' | 'retention' | 'crm' | 'booking', method: string, input: Record<string, any> }
 *   | { kind: 'EXTERNAL', effectId: string, externalKind: 'HTTP_REQUEST' | 'CALL_WEBHOOK', nodeId: string, request: Record<string, any> }
 *   | { kind: 'SET_WAIT', resumeKind: 'REMINDER' | 'TIMEOUT' | 'EFFECT' | null, resumeAt: string | null }
 *   | { kind: 'SET_THREAD_STATE', botState: 'BOT' | 'HUMAN' | null, mutedUntil?: string | null, handoffAt?: string | null }} BotEffect
 * @typedef {{ botId: string, nodeId: string, nodeType: string, branchKey: string | null, status: 'PASSED' | 'SKIPPED' | 'INVALID' | 'TIMEOUT' | 'FAILED',
 *   reason?: string | null, fieldsWritten?: string[] }} BotStepLog
 * @typedef {{ run: BotRunState, effects: BotEffect[], steps: BotStepLog[], arrivals: Array<{ botId: string, nodeId: string }>,
 *   counters: { ticketsOpened: number, cardsCreated: number }, ended: null | { status: string, reason: string } }} BotStepOutput
 */

const TIME_ZONE = 'America/Sao_Paulo';
const MINUTE = 60_000;
// o tempo limite nunca passa do fim da janela de 24 h menos 5 minutos (seção 12, regra 5)
const WINDOW_SAFETY_MILLISECONDS = 5 * MINUTE;
const IDENTITY_RANK = Object.freeze(/** @type {Record<string, number>} */ ({ UNKNOWN: 0, PHONE_MATCH: 1, WEAK_DATA: 2, STRONG_CODE: 3, TEAM: 3 }));
const HUMAN_OPTION_ID = 'human';
const OTHER_OPTION_ID = 'other';
const WHOLE_FAMILY_OPTION_ID = 'whole';
const MORE_SLOTS_OPTION_ID = 'more';
const MAXIMUM_DYNAMIC_OPTIONS = 10;
// lista do WhatsApp: até 10 linhas; com mais horários, 9 e a linha "Ver outros dias"
const MAXIMUM_VISIT_ROWS = WHATSAPP_INTERACTIVE_LIMITS.LIST_ROWS_MAXIMUM;
// horário preenchido por outra pessoa entre a lista e a escolha: oferece de novo até 2 vezes, depois o ramo slotTaken
const MAXIMUM_SLOT_TAKEN_RETRIES = 2;
const VERIFICATION_MAXIMUM_ATTEMPTS = 5;
const SHORT_WEEKDAYS = Object.freeze(['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']);

/**
 * Estado inicial de uma execução (o executor grava antes do START).
 * @param {{ id: string, botId: string, revision: number }} input @returns {BotRunState}
 */
export function createBotRunState(input) {
  return {
    id: input.id, botId: input.botId, revision: input.revision, activeBotId: input.botId, activeRevision: input.revision, currentNodeId: null,
    waitingFor: null, waitingNodeId: null, attempts: 0, stack: [], variables: {}, stepCount: 0, splitSeed: input.id, phase: null, offered: [],
    reminderSent: false, timeoutAt: null, pendingEffect: null, effectSequence: 0, answers: [], sensitiveVariables: [], stepOutputs: {}, cardId: null,
    ticket: null, team: null, visitDateTime: null, verification: null, trigger: null, threadPurpose: null, identityLevel: null, speaker: null,
    students: [], pendingWeak: null, weakFailures: 0,
  };
}

/**
 * Palavra global da mensagem inteira (seção 12, regra 3): "menu" volta ao começo, "atendente" chama uma pessoa.
 * @param {import('./bot-definition.js').BotSettings | null | undefined} settings @param {unknown} text
 * @returns {'MENU' | 'HUMAN' | null}
 */
export function detectGlobalWord(settings, text) {
  const normalized = normalizeBotKeyword(String(text ?? ''));
  if (normalized === '') return null;
  if ((settings?.globalWords?.menu ?? []).map(normalizeBotKeyword).includes(normalized)) return 'MENU';
  if ((settings?.globalWords?.human ?? []).map(normalizeBotKeyword).includes(normalized)) return 'HUMAN';
  return null;
}

/**
 * Departamentos citados na definição (condições de horário, transferência, abrir atendimento), para o executor
 * montar `context.teamOpen` e `context.teamsById`.
 * @param {BotDefinition} definition @returns {string[]}
 */
export function collectReferencedTeamIds(definition) {
  /** @type {Set<string>} */
  const teamIds = new Set();
  const add = (/** @type {unknown} */ id) => {
    if (typeof id === 'string' && id !== '') teamIds.add(id);
  };
  for (const trigger of definition?.triggers ?? []) {
    add(trigger.teamId);
    for (const id of trigger.teamIds ?? []) add(id);
  }
  add(definition?.settings?.humanHandoff && 'teamId' in definition.settings.humanHandoff ? definition.settings.humanHandoff.teamId : null);
  /** @param {BotNode[]} nodes */
  const visit = (nodes) => {
    for (const node of nodes ?? []) {
      add(node.config?.teamId);
      add(node.config?.target?.teamId);
      for (const rule of node.config?.rules ?? []) add(rule?.teamId);
      for (const branch of Object.values(node.branches ?? {})) visit(branch);
    }
  };
  visit(definition?.nodes ?? []);
  return [...teamIds];
}

/** FNV-1a de 32 bits (a escolha da divisão é estável por execução, como spreadInWindow dos fluxos). @param {string} text */
function fnv1a(text) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/** @param {string} now @param {number} milliseconds */
const addMilliseconds = (now, milliseconds) => new Date(Date.parse(now) + milliseconds).toISOString();

/**
 * Anda uma execução com um evento (seção 7.1).
 * @param {{ definition: BotDefinition, run: BotRunState, event: BotEvent, context?: BotContext, now: string,
 *   limits?: { nodesPerEvent?: number, stepsPerRun?: number } }} input
 * @returns {BotStepOutput}
 */
export function stepBotRun(input) {
  const machine = new BotMachine(input);
  machine.handle(input.event);
  return machine.output();
}

class BotMachine {
  /** @param {Parameters<typeof stepBotRun>[0]} input */
  constructor(input) {
    /** @type {BotRunState} */
    this.run = JSON.parse(JSON.stringify(input.run));
    this.run.activeBotId ??= this.run.botId;
    this.run.stack ??= [];
    this.run.variables ??= {};
    this.run.answers ??= [];
    this.run.sensitiveVariables ??= [];
    this.run.stepOutputs ??= {};
    this.run.offered ??= [];
    this.run.effectSequence ??= 0;
    this.rootDefinition = input.definition;
    /** @type {BotContext} */
    this.context = input.context ?? {};
    this.now = input.now;
    this.nodesPerEvent = input.limits?.nodesPerEvent ?? MAXIMUM_BOT_NODES_PER_EVENT;
    this.stepsPerRun = input.limits?.stepsPerRun ?? MAXIMUM_BOT_STEPS_PER_RUN;
    this.nodesThisEvent = 0;
    /** @type {BotEffect[]} */
    this.effects = [];
    /** @type {BotStepLog[]} */
    this.steps = [];
    /** @type {Array<{ botId: string, nodeId: string }>} */
    this.arrivals = [];
    this.counters = { ticketsOpened: 0, cardsCreated: 0 };
    /** @type {null | { status: string, reason: string }} */
    this.ended = null;
    /** @type {Map<string, Map<string, import('./bot-definition.js').IndexedBotNode>>} */
    this.indexes = new Map();
    this.isStopped = false;
  }

  /** @returns {BotStepOutput} */
  output() {
    return { run: this.run, effects: this.effects, steps: this.steps, arrivals: this.arrivals, counters: this.counters, ended: this.ended };
  }

  // ---------------------------------------------------------------------------------------------------------------
  // definições, índice e escopo

  /** @param {string} botId @returns {BotDefinition | null} */
  definitionOf(botId) {
    if (botId === this.run.botId) return this.rootDefinition;
    const bots = this.context.botsById;
    const entry = bots instanceof Map ? bots.get(botId) : bots?.[botId];
    return entry?.definition ?? null;
  }

  /** @param {string} botId */
  botNameOf(botId) {
    if (botId === this.run.botId) return this.context.botName ?? '';
    const bots = this.context.botsById;
    const entry = bots instanceof Map ? bots.get(botId) : bots?.[botId];
    return entry?.name ?? '';
  }

  /** @param {string} botId */
  indexOf(botId) {
    let index = this.indexes.get(botId);
    if (!index) {
      index = indexBotNodes(/** @type {BotDefinition} */ (this.definitionOf(botId) ?? { nodes: [] }));
      this.indexes.set(botId, index);
    }
    return index;
  }

  get activeBotId() {
    return /** @type {string} */ (this.run.activeBotId ?? this.run.botId);
  }

  /** @param {string | null} nodeId @returns {BotNode | null} */
  nodeOf(nodeId) {
    return nodeId ? this.indexOf(this.activeBotId).get(nodeId)?.node ?? null : null;
  }

  get settings() {
    return this.rootDefinition.settings;
  }

  /** Escopo das variáveis para os textos (seção 6). @returns {import('./bot-variables.js').BotTextScope} */
  scope() {
    const run = this.run;
    const student = run.students?.[0] ?? null;
    const speaker = run.speaker ?? (this.context.guardian?.personId ? { firstName: this.context.guardian.firstName ?? null, name: this.context.guardian.name ?? null } : null);
    return {
      contact: this.context.contact ?? null,
      guardian: speaker ? { firstName: speaker.firstName, name: speaker.name } : null,
      student: student ? { firstName: student.firstName, name: student.firstName, gradeLevel: student.gradeLevelLabel, classOffering: null } : null,
      ticket: run.ticket ?? this.context.ticket ?? null,
      team: run.team ? { name: run.team.name, nextOpeningAt: run.team.nextOpeningAt } : null,
      institution: { name: this.context.institutionName ?? null },
      card: { stageLabel: this.context.card?.stageLabel ?? null, ...(run.cardId ? { id: run.cardId } : {}) },
      visit: { dateTime: run.visitDateTime ?? null, timeZone: run.visitTimeZone ?? null },
      verification: { destination: run.verification?.destination ?? null },
      trigger: run.trigger ?? null,
      bot: run.variables,
      steps: run.stepOutputs,
      sensitiveVariables: run.sensitiveVariables,
      now: this.now,
    };
  }

  /** @param {string | null | undefined} text */
  render(text) {
    return renderBotText(text, this.scope());
  }

  get threadPurpose() {
    return this.run.threadPurpose ?? this.context.threadPurpose ?? 'CAPTURE';
  }

  get identityLevel() {
    return this.run.identityLevel ?? this.context.identityLevel ?? 'UNKNOWN';
  }

  // ---------------------------------------------------------------------------------------------------------------
  // efeitos e passos

  /** @param {BotOutgoingMessage} message @param {string} displayText */
  send(message, displayText) {
    this.effects.push({ kind: 'SEND', message, displayText, nodeId: this.run.currentNodeId, botId: this.activeBotId });
  }

  /** @param {string} text */
  sendText(text) {
    const body = String(text ?? '').trim();
    if (body !== '') this.send({ kind: 'TEXT', body: body.slice(0, WHATSAPP_INTERACTIVE_LIMITS.TEXT_BODY) }, body);
  }

  /** @param {BotNode} node @param {BotStepLog['status']} status @param {string | null} branchKey @param {{ reason?: string | null, fieldsWritten?: string[] }} [extra] */
  log(node, status, branchKey, extra = {}) {
    /** @type {BotStepLog} */
    const step = { botId: this.activeBotId, nodeId: node.id, nodeType: node.type, branchKey, status };
    if (extra.reason) step.reason = extra.reason;
    if (extra.fieldsWritten && extra.fieldsWritten.length > 0) step.fieldsWritten = extra.fieldsWritten;
    this.steps.push(step);
  }

  /**
   * Pede uma chamada a uma porta; a execução espera o EFFECT_RESULT na mesma rodada.
   * @param {'serviceDesk' | 'retention' | 'crm' | 'booking'} port @param {string} method @param {Record<string, any>} portInput
   * @param {string} purpose @param {any} [data]
   * @returns {'STOP'}
   */
  callPort(port, method, portInput, purpose, data = null) {
    this.run.effectSequence = (this.run.effectSequence ?? 0) + 1;
    const effectId = `${this.run.id}:${this.run.effectSequence}`;
    this.run.pendingEffect = { effectId, nodeId: this.run.currentNodeId, purpose, data };
    this.run.waitingFor = 'EFFECT';
    this.effects.push({ kind: 'PORT_CALL', effectId, port, method, input: { threadId: this.context.threadId ?? null, ...portInput } });
    return /** @type {'STOP'} */ ('STOP');
  }

  /** @param {string} status @param {string} reason */
  finish(status, reason) {
    this.ended = { status, reason };
    this.run.waitingFor = null;
    this.run.waitingNodeId = null;
    this.run.pendingEffect = null;
    this.run.phase = null;
    this.run.offered = [];
    this.run.pendingWeak = null;
    this.effects.push({ kind: 'SET_WAIT', resumeKind: null, resumeAt: null });
    this.isStopped = true;
  }

  // ---------------------------------------------------------------------------------------------------------------
  // eventos

  /** @param {BotEvent} event */
  handle(event) {
    const windowEndsAt = this.context.windowEndsAt ? Date.parse(this.context.windowEndsAt) : null;
    const isWindowClosed = windowEndsAt !== null && Date.parse(this.now) >= windowEndsAt;
    switch (event.kind) {
      case 'START':
        this.run.trigger = event.trigger ? { keyword: event.trigger.keyword ?? null, buttonText: event.trigger.buttonText ?? null, kind: event.trigger.kind ?? null } : null;
        // fora do horário: {{team.*}} é o departamento do gatilho (seção 6)
        if (event.trigger?.teamId) {
          const teamId = String(event.trigger.teamId);
          this.run.team = { id: teamId, name: this.context.teamsById?.[teamId]?.name ?? null, nextOpeningAt: this.context.teamOpen?.[teamId]?.nextOpeningAt ?? null };
        }
        this.startAt(this.rootDefinition.nodes?.[0]?.id ?? null);
        return;
      case 'MESSAGE':
        if (this.run.waitingFor !== 'ANSWER') return;
        if (event.isGlobalWord === 'MENU') {
          this.restartFromMenu();
          return;
        }
        if (event.isGlobalWord === 'HUMAN') {
          this.handoffToHuman('HUMAN');
          return;
        }
        this.answer(event);
        return;
      case 'REMINDER':
        if (isWindowClosed) {
          this.finish('TIMED_OUT', 'WINDOW_CLOSED');
          return;
        }
        if (this.run.waitingFor !== 'ANSWER' || this.run.reminderSent) return;
        this.run.reminderSent = true;
        this.sendText(this.render(this.settings.reminderText));
        this.effects.push({ kind: 'SET_WAIT', resumeKind: 'TIMEOUT', resumeAt: this.run.timeoutAt ?? this.now });
        return;
      case 'TIMEOUT':
        this.timeout(isWindowClosed);
        return;
      case 'LEGAL_RESTRICTION':
        this.clearWait();
        this.run.pendingEffect = null;
        this.startHandoff(this.humanTarget(), null, 'LEGAL');
        return;
      case 'EFFECT_RESULT':
        if (!this.run.pendingEffect || this.run.pendingEffect.effectId !== event.effectId) return;
        if (isWindowClosed && this.effects.length === 0 && this.run.pendingEffect.purpose === 'EXTERNAL') {
          this.finish('TIMED_OUT', 'WINDOW_CLOSED');
          return;
        }
        this.resumeEffect(event.result);
        return;
      default:
    }
  }

  restartFromMenu() {
    this.run.stack = [];
    this.run.activeBotId = this.run.botId;
    this.run.activeRevision = this.run.revision;
    this.run.attempts = 0;
    this.clearWait();
    this.startAt(this.rootDefinition.nodes?.[0]?.id ?? null);
  }

  clearWait() {
    this.run.waitingFor = null;
    this.run.waitingNodeId = null;
    this.run.phase = null;
    this.run.offered = [];
    this.run.reminderSent = false;
    this.run.timeoutAt = null;
  }

  /** @param {boolean} isWindowClosed */
  timeout(isWindowClosed) {
    const node = this.nodeOf(this.run.waitingNodeId ?? this.run.currentNodeId);
    if (this.run.waitingFor === 'EFFECT' && this.run.pendingEffect?.purpose === 'EXTERNAL' && node) {
      // integração sem resposta em 15 s: segue pelo ramo de falha (seção 4.18)
      this.run.pendingEffect = null;
      this.run.waitingFor = null;
      if (isWindowClosed) {
        this.finish('TIMED_OUT', 'WINDOW_CLOSED');
        return;
      }
      this.log(node, 'FAILED', 'failed', { reason: 'INTEGRATION_TIMEOUT' });
      this.proceed(node, 'failed');
      return;
    }
    if (this.run.waitingFor !== 'ANSWER' || !node) return;
    const hasTimeoutBranch = (node.branches?.timeout ?? []).length > 0;
    this.log(node, 'TIMEOUT', hasTimeoutBranch && !isWindowClosed ? 'timeout' : null);
    if (isWindowClosed) {
      this.finish('TIMED_OUT', 'WINDOW_CLOSED');
      return;
    }
    this.clearWait();
    if (hasTimeoutBranch) {
      this.proceed(node, 'timeout');
      return;
    }
    this.sendText(this.render(this.settings.timeoutText));
    this.finish('TIMED_OUT', 'TIMEOUT');
  }

  // ---------------------------------------------------------------------------------------------------------------
  // andar pela árvore

  /** @param {string | null} nodeId */
  startAt(nodeId) {
    if (!nodeId) {
      this.endOfPath();
      return;
    }
    this.walk(nodeId);
  }

  /** @param {string} startNodeId */
  walk(startNodeId) {
    /** @type {string | null} */
    let nodeId = startNodeId;
    while (nodeId && !this.isStopped) {
      const node = this.nodeOf(nodeId);
      if (!node) {
        this.finish('FAILED', 'ERROR');
        return;
      }
      this.nodesThisEvent += 1;
      this.run.stepCount += 1;
      if (this.nodesThisEvent > this.nodesPerEvent || this.run.stepCount > this.stepsPerRun) {
        this.log(node, 'FAILED', null, { reason: 'LOOP_LIMIT' });
        this.handoffToHuman('LOOP_LIMIT');
        return;
      }
      this.run.currentNodeId = node.id;
      this.run.attempts = 0;
      this.run.phase = null;
      this.arrivals.push({ botId: this.activeBotId, nodeId: node.id });
      const result = this.enter(node);
      if (result === 'STOP' || this.isStopped) return;
      if (result && typeof result === 'object' && 'jump' in result) {
        nodeId = result.jump;
        continue;
      }
      const branchKey = result && typeof result === 'object' && 'branch' in result ? result.branch ?? null : null;
      nodeId = this.nextAfter(node, branchKey);
      if (!nodeId) {
        this.endOfPath();
        return;
      }
    }
  }

  /** @param {BotNode} node @param {string | null} branchKey */
  nextAfter(node, branchKey) {
    return findNextBotNodeId(this.indexOf(this.activeBotId), node.id, branchKey);
  }

  /** Continua depois do bloco (resposta, efeito) pelo ramo. @param {BotNode} node @param {string | null} branchKey */
  proceed(node, branchKey) {
    if (this.isStopped) return;
    this.clearWait();
    const next = this.nextAfter(node, branchKey);
    if (next) this.walk(next);
    else this.endOfPath();
  }

  /** Fim da lista: volta ao bot que chamou o subbot ou termina (seção 4.7). */
  endOfPath() {
    if (this.returnFromSubbot()) return;
    this.finish('COMPLETED', 'END_OF_PATH');
  }

  /** @returns {boolean} */
  returnFromSubbot() {
    const frame = this.run.stack.pop();
    if (!frame) return false;
    this.run.activeBotId = frame.botId;
    this.run.activeRevision = frame.revision;
    const caller = this.nodeOf(frame.returnNodeId);
    if (!caller) {
      this.finish('FAILED', 'ERROR');
      return true;
    }
    this.run.currentNodeId = caller.id;
    this.log(caller, 'PASSED', 'returned');
    this.proceed(caller, 'returned');
    return true;
  }

  // ---------------------------------------------------------------------------------------------------------------
  // espera da resposta

  /**
   * @param {BotNode} node @param {{ phase?: string | null, offered?: OfferedOption[], keepAttempts?: boolean }} [options]
   * @returns {'STOP'}
   */
  waitForAnswer(node, options = {}) {
    const run = this.run;
    run.waitingFor = 'ANSWER';
    run.waitingNodeId = node.id;
    run.currentNodeId = node.id;
    run.phase = options.phase ?? run.phase ?? null;
    if (options.offered) run.offered = options.offered;
    run.reminderSent = false;
    const timeoutMinutes = Number(node.config?.timeoutMinutes ?? node.config?.options?.timeoutMinutes ?? this.settings.questionTimeoutMinutes ?? 30);
    let timeoutAt = Date.parse(this.now) + timeoutMinutes * MINUTE;
    if (this.context.windowEndsAt) timeoutAt = Math.min(timeoutAt, Date.parse(this.context.windowEndsAt) - WINDOW_SAFETY_MILLISECONDS);
    if (timeoutAt <= Date.parse(this.now)) {
      // a janela fecha antes de dar tempo de responder: nada é retomado depois de 24 h (seção 12, regra 6)
      this.finish('TIMED_OUT', 'WINDOW_CLOSED');
      return /** @type {'STOP'} */ ('STOP');
    }
    run.timeoutAt = new Date(timeoutAt).toISOString();
    const reminderMinutes = this.settings.reminderAfterMinutes;
    const reminderAt = reminderMinutes && this.settings.reminderText ? Date.parse(this.now) + reminderMinutes * MINUTE : null;
    if (reminderAt !== null && reminderAt < timeoutAt) this.effects.push({ kind: 'SET_WAIT', resumeKind: 'REMINDER', resumeAt: new Date(reminderAt).toISOString() });
    else this.effects.push({ kind: 'SET_WAIT', resumeKind: 'TIMEOUT', resumeAt: run.timeoutAt });
    return /** @type {'STOP'} */ ('STOP');
  }

  /** @param {BotNode} node */
  retriesOf(node) {
    const value = node.config?.retries ?? node.config?.options?.retries ?? this.settings.retries ?? DEFAULT_BOT_RETRIES;
    return Number.isInteger(value) ? Number(value) : DEFAULT_BOT_RETRIES;
  }

  /**
   * Resposta que não serviu: manda o texto de nova tentativa e espera de novo, até as tentativas; depois, `invalid`.
   * @param {BotNode} node @param {string} reason @param {string | null} [retryText]
   */
  invalidAnswer(node, reason, retryText = null) {
    this.run.attempts += 1;
    if (this.run.attempts > this.retriesOf(node)) {
      // bloco sem ramo de inválida (identificação, código): segue pelo ramo de não encontrado ou de falha
      // agendar visita sem escolha válida: como sem horário que sirva
      const branch = ['invalid', 'notFound', 'failed', 'none', 'noSlots'].find((key) => node.branches && key in node.branches) ?? null;
      this.log(node, 'INVALID', branch, { reason });
      this.proceed(node, branch);
      return;
    }
    this.log(node, 'INVALID', null, { reason });
    this.sendText(this.render(retryText ?? node.config?.retryText ?? this.settings.invalidText));
    this.waitForAnswer(node, { keepAttempts: true });
  }

  /**
   * Opções como botões (até 3) ou lista, com o id que volta para esta execução e este bloco.
   * @param {BotNode} node @param {string} body @param {OfferedOption[]} offered @param {{ presentation?: string | null, header?: string | null,
   *   footer?: string | null, listButtonLabel?: string | null }} [options]
   */
  sendOptions(node, body, offered, options = {}) {
    const isList = options.presentation === 'LIST' || offered.length > WHATSAPP_INTERACTIVE_LIMITS.BUTTONS_MAXIMUM;
    const encode = (/** @type {string} */ optionId) => encodeBotOptionId(this.run.id, node.id, optionId);
    const text = body.trim() === '' ? ' ' : body;
    /** @type {BotOutgoingMessage} */
    const message = isList
      ? {
        kind: 'LIST', body: text.slice(0, WHATSAPP_INTERACTIVE_LIMITS.LIST_BODY), header: options.header ?? null, footer: options.footer ?? null,
        button: truncateTitle(options.listButtonLabel ?? BOT_DEFAULT_TEXTS.listButton, WHATSAPP_INTERACTIVE_LIMITS.LIST_BUTTON),
        sections: [{
          rows: offered.map((option) => ({
            id: encode(option.id), title: truncateTitle(option.title, WHATSAPP_INTERACTIVE_LIMITS.LIST_ROW_TITLE),
            ...(option.description ? { description: truncateTitle(option.description, WHATSAPP_INTERACTIVE_LIMITS.LIST_ROW_DESCRIPTION) } : {}),
          })),
        }],
      }
      : {
        kind: 'BUTTONS', body: text.slice(0, WHATSAPP_INTERACTIVE_LIMITS.BUTTONS_BODY), header: options.header ?? null, footer: options.footer ?? null,
        buttons: offered.map((option) => ({ id: encode(option.id), title: truncateTitle(option.title, WHATSAPP_INTERACTIVE_LIMITS.BUTTON_TITLE) })),
      };
    const titles = offered.map((option, index) => `${index + 1}. ${option.title}`);
    this.send(message, [body.trim(), ...titles].filter(Boolean).join('\n'));
  }

  /**
   * Opção escolhida na resposta, entre as oferecidas.
   * @param {BotNode} node @param {Extract<BotEvent, { kind: 'MESSAGE' }>} event
   */
  matchOffered(node, event) {
    return matchInteractiveAnswer(this.run.offered ?? [], { interactiveId: event.interactiveId ?? null, text: event.text ?? null, runId: this.run.id, nodeId: node.id });
  }

  /** @param {BotNode} node @param {string} value @param {boolean} isSensitive */
  rememberAnswer(node, value, isSensitive) {
    const title = node.name ?? truncateTitle(String(node.config?.text ?? node.type).replace(/\{\{[^}]*\}\}/g, '').trim() || node.type, 60);
    this.run.answers = [...(this.run.answers ?? []), { title, value: String(value ?? '').slice(0, 200), isSensitive }];
  }

  /** @param {string} name @param {unknown} value @param {boolean} [isSensitive] */
  setVariable(name, value, isSensitive = false) {
    if (!name) return;
    const text = typeof value === 'string' ? value : JSON.stringify(value ?? '');
    this.run.variables = { ...this.run.variables, [name]: String(text ?? '').slice(0, MAXIMUM_BOT_VARIABLE_VALUE_LENGTH) };
    const sensitive = new Set(this.run.sensitiveVariables ?? []);
    if (isSensitive) sensitive.add(name);
    else sensitive.delete(name);
    this.run.sensitiveVariables = [...sensitive];
  }

  // ---------------------------------------------------------------------------------------------------------------
  // blocos: chegada

  /**
   * Executa o bloco ao chegar. Devolve 'STOP' (espera ou efeito), `{ branch }` para seguir ou `{ jump }` para o GOTO.
   * @param {BotNode} node @returns {'STOP' | { branch?: string | null } | { jump: string } | void}
   */
  enter(node) {
    const config = node.config ?? {};
    switch (node.type) {
      case 'SEND_MESSAGE': return this.enterSendMessage(node);
      case 'ASK':
        this.sendText(this.render(config.text));
        return this.waitForAnswer(node);
      case 'CONDITION': return this.enterCondition(node);
      case 'SPLIT_PERCENT': {
        const shares = Object.entries(config.shares ?? {}).filter(([, value]) => Number(value) > 0);
        const point = fnv1a(`${this.run.splitSeed ?? this.run.id}:${node.id}`) % 100;
        let total = 0;
        let branch = shares[shares.length - 1]?.[0] ?? 'a';
        for (const [key, value] of shares) {
          total += Number(value);
          if (point < total) {
            branch = key;
            break;
          }
        }
        this.log(node, 'PASSED', branch);
        return { branch };
      }
      case 'SET_VARIABLE':
        this.setVariable(String(config.name ?? ''), this.render(config.value));
        this.log(node, 'PASSED', null, { fieldsWritten: [`bot.${config.name}`] });
        return {};
      case 'GOTO':
        if (!config.targetNodeId || !this.nodeOf(config.targetNodeId)) {
          this.log(node, 'FAILED', null, { reason: 'ERROR' });
          this.finish('FAILED', 'ERROR');
          return /** @type {'STOP'} */ ('STOP');
        }
        this.log(node, 'PASSED', null);
        return { jump: config.targetNodeId };
      case 'SUBBOT': return this.enterSubbot(node);
      case 'END':
        if (config.text) this.sendText(this.render(config.text));
        this.log(node, 'PASSED', null);
        if (this.run.stack.length > 0) {
          this.returnFromSubbot();
          return /** @type {'STOP'} */ ('STOP');
        }
        this.finish('COMPLETED', 'END_NODE');
        return /** @type {'STOP'} */ ('STOP');
      case 'IDENTIFY_GUARDIAN':
        return this.callPort('serviceDesk', 'identifyByPhone', {}, 'IDENTIFY_PHONE');
      case 'CHOOSE_STUDENT':
        return this.callPort('serviceDesk', 'listStudentsForSpeaker', {}, 'LIST_STUDENTS');
      case 'STRONG_VERIFICATION': {
        const personId = this.run.speaker?.personId ?? this.context.speakerPersonId ?? null;
        if (!personId) {
          this.log(node, 'PASSED', 'unavailable');
          return { branch: 'unavailable' };
        }
        return this.callPort('serviceDesk', 'requestStrongCode', { personId, purpose: config.purpose ?? 'SENSITIVE_TOPIC' }, 'REQUEST_CODE');
      }
      case 'SET_PURPOSE':
        return this.callPort('serviceDesk', 'settlePendingPurpose', { purpose: config.purpose, teamId: config.teamId ?? null }, 'SET_PURPOSE');
      case 'OPEN_TICKET': return this.enterOpenTicket(node);
      case 'HANDOFF':
        return this.startHandoff(/** @type {BotHandoffTarget} */ (config.target), node, 'NODE');
      case 'CREATE_CAPTURE_CARD': return this.callPort('crm', 'createCaptureCard', this.captureCardInput(node), 'CREATE_CARD');
      case 'UPDATE_STAGE':
      case 'UPDATE_FIELD':
      case 'ADD_TAGS':
      case 'REMOVE_TAGS':
      case 'ASSIGN_OWNER':
      case 'CREATE_TASK':
      case 'ADD_NOTE':
      case 'SEND_NOTIFICATION':
        return this.callPort('crm', 'executeCrmAction', {
          type: node.type, nodeId: node.id, botId: this.activeBotId, botName: this.botNameOf(this.run.botId), runId: this.run.id, cardId: this.run.cardId ?? null,
          ticketId: this.run.ticket?.id ?? this.context.ticket?.id ?? null, speakerPersonId: this.run.speaker?.personId ?? this.context.speakerPersonId ?? null,
          config: this.renderConfig(node),
        }, 'CRM_ACTION');
      case 'SCHEDULE_VISIT':
        this.run.slotTakenCount = 0;
        if (!this.run.cardId) return this.callPort('crm', 'createCaptureCard', this.captureCardInput(null), 'VISIT_CARD');
        return this.listVisitSlots(node, this.now);
      case 'CALL_WEBHOOK':
      case 'HTTP_REQUEST': return this.enterIntegration(node);
      default:
        this.log(node, 'SKIPPED', null, { reason: 'ERROR' });
        return {};
    }
  }

  /** Configuração da ação com os textos já preenchidos (título, corpo, valor). @param {BotNode} node */
  renderConfig(node) {
    const config = { ...(node.config ?? {}) };
    for (const key of ['title', 'body', 'value']) if (typeof config[key] === 'string') config[key] = this.render(config[key]);
    return config;
  }

  /** @param {BotNode | null} node */
  captureCardInput(node) {
    const config = node?.config ?? {};
    const variable = (/** @type {unknown} */ name) => (typeof name === 'string' && name ? this.run.variables[name] ?? null : null);
    return {
      nodeId: node?.id ?? null, runId: this.run.id, botId: this.run.botId, cardId: this.run.cardId ?? null,
      studentName: variable(config.studentNameVariable), courseId: variable(config.courseVariable), academicPeriodId: variable(config.academicPeriodVariable),
      campusId: variable(config.campusVariable), leadSource: variable(config.leadSourceVariable), assign: config.assign ?? 'ROUND_ROBIN',
      workspaceMemberIds: Array.isArray(config.workspaceMemberIds) ? config.workspaceMemberIds : [],
    };
  }

  /** @param {BotNode} node */
  enterSendMessage(node) {
    const config = node.config ?? {};
    const text = this.render(config.text);
    const options = config.options;
    if (config.media?.fileId) {
      const mediaKind = /** @type {'IMAGE' | 'DOCUMENT' | 'AUDIO'} */ (config.media.kind ?? 'IMAGE');
      // áudio não tem legenda: o texto sai antes; com opções, o texto vai no corpo das opções
      if (mediaKind === 'AUDIO' && !options && text) this.sendText(text);
      const caption = mediaKind === 'AUDIO' || options ? null : text;
      this.send({ kind: 'MEDIA', mediaKind, fileId: String(config.media.fileId), caption }, caption ?? '');
    } else if (!options) {
      this.sendText(text);
    }
    if (!options) {
      this.log(node, 'PASSED', null);
      return {};
    }
    if (hasStaticOptions(node)) {
      /** @type {OfferedOption[]} */
      const offered = (options.items ?? []).map((/** @type {any} */ item) => ({ id: String(item.id), title: String(item.title ?? ''), description: item.description ?? null }));
      if (options.addHumanOption) offered.push({ id: HUMAN_OPTION_ID, title: this.settings.humanOptionLabel ?? BOT_DEFAULT_TEXTS.humanOptionLabel });
      this.sendOptions(node, text, offered, options);
      return this.waitForAnswer(node, { offered });
    }
    return this.callPort('crm', 'listDynamicOptions', {
      source: options.source, segmentId: options.segmentVariable ? this.run.variables[options.segmentVariable] ?? null : null,
    }, 'DYNAMIC_OPTIONS');
  }

  /** @param {BotNode} node @param {Array<{ id: string, title: string }>} rows */
  offerDynamicOptions(node, rows) {
    const options = node.config.options ?? {};
    if (!Array.isArray(rows) || rows.length === 0) {
      this.log(node, 'PASSED', 'none');
      this.proceed(node, 'none');
      return;
    }
    /** @type {OfferedOption[]} */
    let offered = rows.map((row) => ({ id: String(row.id), title: String(row.title ?? ''), value: String(row.id) }));
    // uma opção só (a escola tem uma unidade): escolhe sem perguntar
    if (offered.length === 1) {
      this.run.offered = offered;
      this.acceptOption(node, offered[0]);
      return;
    }
    if (offered.length > MAXIMUM_DYNAMIC_OPTIONS) offered = [...offered.slice(0, MAXIMUM_DYNAMIC_OPTIONS - 1), { id: OTHER_OPTION_ID, title: BOT_DEFAULT_TEXTS.otherOption }];
    if (options.addHumanOption && offered.length < MAXIMUM_DYNAMIC_OPTIONS) offered.push({ id: HUMAN_OPTION_ID, title: this.settings.humanOptionLabel ?? BOT_DEFAULT_TEXTS.humanOptionLabel });
    this.sendOptions(node, this.render(node.config.text), offered, options);
    this.waitForAnswer(node, { offered });
  }

  /** @param {BotNode} node */
  enterCondition(node) {
    const rules = Array.isArray(node.config?.rules) ? node.config.rules : [];
    const fieldRules = rules.filter((/** @type {any} */ rule) => rule?.kind === 'FIELD');
    if (fieldRules.length > 0) {
      return this.callPort('crm', 'executeCrmAction', {
        type: 'READ_FIELDS', cardId: this.run.cardId ?? null, ticketId: this.run.ticket?.id ?? this.context.ticket?.id ?? null,
        speakerPersonId: this.run.speaker?.personId ?? this.context.speakerPersonId ?? null,
        fields: fieldRules.map((/** @type {any} */ rule) => ({ target: rule.target, fieldName: rule.fieldName })),
      }, 'CONDITION_FIELDS');
    }
    const branch = this.evaluateRules(node, []) ? 'yes' : 'no';
    this.log(node, 'PASSED', branch);
    return { branch };
  }

  /** @param {BotNode} node @param {unknown[]} fieldValues */
  evaluateRules(node, fieldValues) {
    const rules = Array.isArray(node.config?.rules) ? node.config.rules : [];
    let fieldIndex = 0;
    const results = rules.map((/** @type {any} */ rule) => {
      switch (rule?.kind) {
        case 'VARIABLE': return compareValues(this.run.variables[rule.name] ?? null, rule.operator, rule.value);
        case 'FIELD': {
          const value = fieldValues[fieldIndex];
          fieldIndex += 1;
          return compareValues(value, rule.operator, rule.value);
        }
        case 'TEAM_OPEN': {
          const isOpen = this.context.teamOpen?.[rule.teamId]?.isOpen === true;
          return rule.operator === 'isClosed' ? !isOpen : isOpen;
        }
        case 'CHANNEL': return (rule.channelIds ?? []).includes(this.context.channelId);
        case 'FAMILY_STATUS': return (this.context.familyStatus ?? 'UNKNOWN') === rule.value;
        case 'IDENTITY_LEVEL': return (IDENTITY_RANK[this.identityLevel] ?? 0) >= (IDENTITY_RANK[rule.value] ?? 0);
        case 'TIME_OF_DAY': return isWithinTimeOfDay(this.now, rule);
        default: return false;
      }
    });
    if (results.length === 0) return false;
    return node.config?.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
  }

  /** @param {BotNode} node */
  enterSubbot(node) {
    const targetId = String(node.config?.botId ?? '');
    const definition = targetId ? this.definitionOf(targetId) : null;
    if (!definition || this.run.stack.length >= 3) {
      this.log(node, 'FAILED', null, { reason: 'ERROR' });
      this.finish('FAILED', 'ERROR');
      return /** @type {'STOP'} */ ('STOP');
    }
    const bots = this.context.botsById;
    const entry = bots instanceof Map ? bots.get(targetId) : bots?.[targetId];
    this.run.stack.push({ botId: this.activeBotId, revision: this.run.activeRevision ?? null, returnNodeId: node.id });
    this.run.activeBotId = targetId;
    this.run.activeRevision = entry?.revision ?? null;
    const first = definition.nodes?.[0]?.id ?? null;
    if (!first) {
      this.returnFromSubbot();
      return /** @type {'STOP'} */ ('STOP');
    }
    this.walk(first);
    return /** @type {'STOP'} */ ('STOP');
  }

  /** @param {BotNode} node */
  enterOpenTicket(node) {
    const config = node.config ?? {};
    if (config.ticketType === 'RETENTION') {
      const householdId = this.context.householdId ?? null;
      if (!householdId) {
        this.log(node, 'SKIPPED', null, { reason: 'RECORD_NOT_FOUND' });
        return {};
      }
      return this.callPort('retention', 'openRetentionCase', { householdId, openedBy: 'BOT', note: this.summaryFor(config.summaryTemplate) }, 'OPEN_RETENTION');
    }
    return this.callPort('serviceDesk', 'openTicket', {
      source: 'BOT', channel: 'WHATSAPP', teamId: config.teamId, category: config.category ?? null, priority: config.priority ?? null,
      subject: this.render(config.subjectTemplate).slice(0, 200) || this.botNameOf(this.run.botId), summary: this.summaryFor(config.summaryTemplate),
      requesterPersonId: this.run.speaker?.personId ?? this.context.speakerPersonId ?? null, householdId: this.context.householdId ?? null,
      studentPersonIds: (this.run.students ?? []).map((student) => student.personId), ticketType: 'REQUEST', identityLevel: this.identityLevel,
    }, 'OPEN_TICKET', { teamId: config.teamId });
  }

  /**
   * Horários livres da página de agendamento (frente F6, porta booking, assíncrona): um a mais que cabe na lista, para
   * saber se há "Ver outros dias".
   * @param {BotNode} node @param {string} from
   */
  listVisitSlots(node, from) {
    return this.callPort('booking', 'listBookableSlots', {
      bookingPageId: node.config?.bookingPageId ?? null, from, to: null, limit: MAXIMUM_VISIT_ROWS + 1,
    }, 'VISIT_SLOTS');
  }

  /** @param {BotNode} node */
  enterIntegration(node) {
    const config = node.config ?? {};
    this.run.effectSequence = (this.run.effectSequence ?? 0) + 1;
    const effectId = `${this.run.id}:${this.run.effectSequence}`;
    this.run.pendingEffect = { effectId, nodeId: node.id, purpose: 'EXTERNAL' };
    this.run.waitingFor = 'EFFECT';
    this.run.waitingNodeId = node.id;
    /** @type {Record<string, any>} */
    const request = node.type === 'CALL_WEBHOOK'
      ? { url: config.url, secret: config.secret ?? null }
      : {
        method: config.method ?? 'GET', url: this.render(config.url),
        headers: (config.headers ?? []).map((/** @type {any} */ header) => ({ name: header.name, value: this.render(String(header.value ?? '')), isSecret: header.isSecret === true })),
        bodyType: config.bodyType ?? 'NONE', body: this.renderBody(config.bodyType, config.body),
      };
    this.effects.push({ kind: 'EXTERNAL', effectId, externalKind: /** @type {'HTTP_REQUEST' | 'CALL_WEBHOOK'} */ (node.type), nodeId: node.id, request });
    this.effects.push({ kind: 'SET_WAIT', resumeKind: 'EFFECT', resumeAt: addMilliseconds(this.now, BOT_INTEGRATION_TIMEOUT_MILLISECONDS) });
    return /** @type {'STOP'} */ ('STOP');
  }

  /** Corpo JSON com as variáveis nos textos (o JSON continua válido). @param {string} bodyType @param {unknown} body */
  renderBody(bodyType, body) {
    if (typeof body !== 'string' || body === '') return null;
    if (bodyType !== 'JSON') return this.render(body);
    try {
      return JSON.stringify(renderJsonStrings(JSON.parse(body), (text) => this.render(text)));
    } catch {
      return this.render(body);
    }
  }

  // ---------------------------------------------------------------------------------------------------------------
  // transferência

  /** Destino do "Falar com uma pessoa" e da palavra global: o das configurações ou o da fila da conversa. @returns {BotHandoffTarget} */
  humanTarget() {
    if (this.settings.humanHandoff) return this.settings.humanHandoff;
    if (this.threadPurpose !== 'CAPTURE' && this.context.queueTeamId) return { kind: 'TEAM', teamId: this.context.queueTeamId };
    return { kind: 'CARD_OWNER' };
  }

  /** @param {'HUMAN' | 'LOOP_LIMIT' | 'LEGAL' | 'ERROR'} origin */
  handoffToHuman(origin) {
    this.clearWait();
    const node = this.nodeOf(this.run.currentNodeId);
    if (origin === 'HUMAN' && node) this.log(node, 'PASSED', null, { reason: 'HUMAN_REQUESTED' });
    this.startHandoff(this.humanTarget(), null, origin);
  }

  /**
   * Transfere para uma pessoa (seção 13.1): atendimento pelo departamento, captação pelo responsável do card.
   * @param {BotHandoffTarget} target @param {BotNode | null} node @param {'NODE' | 'HUMAN' | 'LOOP_LIMIT' | 'LEGAL' | 'ERROR'} origin
   */
  startHandoff(target, node, origin) {
    const purpose = this.threadPurpose;
    const summary = this.summaryFor(node?.config?.summaryTemplate ?? null);
    const teamId = target?.kind === 'TEAM' ? target.teamId : ('teamId' in (target ?? {}) ? /** @type {any} */ (target).teamId : null) ?? null;
    const isServiceTarget = target?.kind === 'TEAM' || (purpose === 'SERVICE' && Boolean(teamId ?? this.context.queueTeamId));
    const data = { origin, nodeId: node?.id ?? null, target };
    if (isServiceTarget) {
      return this.callPort('serviceDesk', 'handoffThreadToTeam', {
        teamId: teamId ?? this.context.queueTeamId, workspaceMemberId: target?.kind === 'MEMBER' ? target.workspaceMemberId : null, summary, subject: this.handoffSubject(), botRunId: this.run.id,
        priority: node?.config?.priority ?? null, category: node?.config?.category ?? null,
      }, 'HANDOFF_SERVICE', data);
    }
    return this.callPort('crm', 'executeCrmAction', {
      type: 'HANDOFF_CAPTURE', target: target ?? { kind: 'CARD_OWNER' }, summary, runId: this.run.id, botId: this.run.botId, botName: this.botNameOf(this.run.botId),
      cardId: this.run.cardId ?? null,
    }, 'HANDOFF_CAPTURE', data);
  }

  /** @param {{ origin: string, nodeId: string | null }} data */
  finishHandoff(data) {
    const node = data.nodeId ? this.nodeOf(data.nodeId) : null;
    if (node) this.log(node, 'PASSED', null);
    if (data.origin === 'LOOP_LIMIT') this.finish('FAILED', 'LOOP_LIMIT');
    else if (data.origin === 'ERROR') this.finish('FAILED', 'ERROR');
    else if (data.origin === 'LEGAL') this.finish('HANDED_OFF', 'LEGAL_RESTRICTION');
    else this.finish('HANDED_OFF', 'HANDOFF');
  }

  /**
   * Resumo para a equipe (seção 13.3): o modelo da escola ou as respostas na ordem, as sensíveis como "informado".
   * @param {string | null | undefined} template
   */
  summaryFor(template) {
    if (template) return this.render(template).slice(0, MAXIMUM_BOT_SUMMARY_LENGTH);
    const botName = this.botNameOf(this.run.botId);
    const lines = (this.run.answers ?? []).map((answer) => `Bot ${botName}: ${answer.title}: ${answer.isSensitive ? BOT_DEFAULT_TEXTS.sensitiveValue : answer.value}`);
    const speaker = this.run.speaker;
    if (speaker?.name || speaker?.firstName) lines.push(`Quem fala: ${speaker.name ?? speaker.firstName}`);
    if (this.identityLevel && this.identityLevel !== 'UNKNOWN') lines.push(`Identificação: ${BOT_IDENTITY_LEVEL_LABELS[this.identityLevel] ?? this.identityLevel}`);
    const students = (this.run.students ?? []).map((student) => student.firstName).filter(Boolean);
    if (students.length > 0) lines.push(`Alunos: ${students.join(', ')}`);
    return lines.join('\n').slice(0, MAXIMUM_BOT_SUMMARY_LENGTH);
  }

  /**
   * Assunto do atendimento aberto pela transferência: a última resposta da família que não é sensível (o pedido em
   * poucas palavras, ou o assunto escolhido no menu); sem nenhuma, o motor usa "Atendimento <departamento>".
   */
  handoffSubject() {
    const answer = [...(this.run.answers ?? [])].reverse().find((item) => !item.isSensitive && String(item.value ?? '').trim() !== '');
    return answer ? String(answer.value).trim().slice(0, SUBJECT_MAX_LENGTH) : null;
  }

  // ---------------------------------------------------------------------------------------------------------------
  // respostas

  /** @param {Extract<BotEvent, { kind: 'MESSAGE' }>} event */
  answer(event) {
    const node = this.nodeOf(this.run.waitingNodeId);
    if (!node) {
      this.finish('FAILED', 'ERROR');
      return;
    }
    this.run.currentNodeId = node.id;
    const matched = this.run.offered && this.run.offered.length > 0 ? this.matchOffered(node, event) : null;
    if (matched === HUMAN_OPTION_ID) {
      this.handoffToHuman('HUMAN');
      return;
    }
    switch (node.type) {
      case 'SEND_MESSAGE': {
        if (this.run.phase === 'OTHER_TEXT') {
          const text = String(event.text ?? '').trim();
          if (text === '') {
            this.invalidAnswer(node, 'NOT_TEXT');
            return;
          }
          this.acceptOption(node, { id: OTHER_OPTION_ID, title: text, value: text });
          return;
        }
        if (matched === OTHER_OPTION_ID) {
          this.sendText(BOT_DEFAULT_TEXTS.otherAnswerPrompt);
          this.waitForAnswer(node, { phase: 'OTHER_TEXT' });
          return;
        }
        const option = (this.run.offered ?? []).find((item) => item.id === matched);
        if (option) {
          this.acceptOption(node, option);
          return;
        }
        // lista dinâmica cortada com "Outro": texto livre vale como a resposta
        const text = String(event.text ?? '').trim();
        if (!event.interactiveId && text !== '' && (this.run.offered ?? []).some((item) => item.id === OTHER_OPTION_ID)) {
          this.acceptOption(node, { id: OTHER_OPTION_ID, title: text, value: text });
          return;
        }
        this.invalidAnswer(node, 'OPTION_NOT_FOUND');
        return;
      }
      case 'ASK': return this.answerAsk(node, event);
      case 'IDENTIFY_GUARDIAN': return this.answerIdentify(node, event, matched);
      case 'CHOOSE_STUDENT': {
        if (!matched) {
          this.invalidAnswer(node, 'OPTION_NOT_FOUND');
          return;
        }
        const isWholeFamily = matched === WHOLE_FAMILY_OPTION_ID;
        const chosen = isWholeFamily ? (this.run.offered ?? []).filter((item) => item.id !== WHOLE_FAMILY_OPTION_ID && item.id !== HUMAN_OPTION_ID) : (this.run.offered ?? []).filter((item) => item.id === matched);
        this.chooseStudents(node, chosen.map((item) => item.id), isWholeFamily);
        return;
      }
      case 'STRONG_VERIFICATION': {
        const code = String(event.text ?? '').replace(/\D/g, '');
        if (code.length !== 6) {
          this.verificationRetry(node);
          return;
        }
        this.callPort('serviceDesk', 'verifyStrongCode', { codeId: this.run.verification?.codeId ?? null, code }, 'VERIFY_CODE');
        return;
      }
      case 'SCHEDULE_VISIT': {
        if (matched === MORE_SLOTS_OPTION_ID && this.run.visitNextFrom) {
          this.listVisitSlots(node, this.run.visitNextFrom);
          return;
        }
        const slot = (this.run.offered ?? []).find((item) => item.id === matched && item.id !== MORE_SLOTS_OPTION_ID);
        if (!slot) {
          this.invalidAnswer(node, 'OPTION_NOT_FOUND');
          return;
        }
        this.callPort('booking', 'bookSlot', {
          bookingPageId: node.config?.bookingPageId ?? null, startsAt: slot.value, personId: this.run.speaker?.personId ?? this.context.speakerPersonId ?? null,
          householdId: this.context.householdId ?? null, enrollmentOpportunityId: this.run.cardId ?? null, answers: this.bookingAnswers(), source: 'BOT',
        }, 'VISIT_BOOK', { startsAt: slot.value });
        return;
      }
      default:
    }
  }

  /** @param {BotNode} node @param {OfferedOption} option */
  acceptOption(node, option) {
    const options = node.config?.options ?? {};
    const isDynamic = hasDynamicOptions(node);
    const saveTo = options.saveTo ?? null;
    const value = isDynamic ? option.value ?? option.title : option.title;
    if (saveTo?.variable) this.setVariable(saveTo.variable, value);
    this.rememberAnswer(node, option.title, false);
    const branch = isDynamic ? 'answered' : option.id;
    if (saveTo?.target && saveTo?.fieldName) {
      this.callPort('crm', 'writeAnswerField', {
        target: saveTo.target, fieldName: saveTo.fieldName, value, display: option.title, inputKind: isDynamic ? 'RELATION' : 'TEXT', cardId: this.run.cardId ?? null,
        speakerPersonId: this.run.speaker?.personId ?? this.context.speakerPersonId ?? null, nodeId: node.id, botId: this.run.botId,
      }, 'WRITE_FIELD', { branch });
      return;
    }
    this.log(node, 'PASSED', branch, { fieldsWritten: saveTo?.variable ? [`bot.${saveTo.variable}`] : [] });
    this.proceed(node, branch);
  }

  /** @param {BotNode} node @param {Extract<BotEvent, { kind: 'MESSAGE' }>} event */
  answerAsk(node, event) {
    const config = node.config ?? {};
    const result = validateBotAnswer(config.inputKind ?? 'TEXT', event.text ?? '', {
      ...(config.validation ?? {}), today: localDateOf(this.now), isTextMessage: event.isTextMessage, attachments: event.attachments ?? [],
    });
    if (!result.ok) {
      this.invalidAnswer(node, result.reason);
      return;
    }
    const isSensitive = config.isSensitive ?? (config.inputKind === 'CPF' || config.inputKind === 'DATE');
    const variable = config.saveTo?.variable;
    if (variable) this.setVariable(variable, String(result.value), Boolean(isSensitive));
    this.rememberAnswer(node, result.display, Boolean(isSensitive));
    if (config.saveTo?.target && config.saveTo?.fieldName) {
      this.callPort('crm', 'writeAnswerField', {
        target: config.saveTo.target, fieldName: config.saveTo.fieldName, value: result.value, display: result.display, inputKind: config.inputKind,
        cardId: this.run.cardId ?? null, speakerPersonId: this.run.speaker?.personId ?? this.context.speakerPersonId ?? null, nodeId: node.id, botId: this.run.botId,
      }, 'WRITE_FIELD', { branch: 'answered' });
      return;
    }
    this.log(node, 'PASSED', 'answered', { fieldsWritten: variable ? [`bot.${variable}`] : [] });
    this.proceed(node, 'answered');
  }

  /** @param {BotNode} node @param {Extract<BotEvent, { kind: 'MESSAGE' }>} event @param {string | null} matched */
  answerIdentify(node, event, matched) {
    const texts = node.config?.texts ?? {};
    if (this.run.phase === 'ASK_WHO') {
      if (!matched) {
        this.invalidAnswer(node, 'OPTION_NOT_FOUND');
        return;
      }
      const candidate = (this.run.offered ?? []).find((item) => item.id === matched);
      this.callPort('serviceDesk', 'confirmSpeaker', { personId: matched }, 'CONFIRM_SPEAKER', { personId: matched, firstName: candidate?.title ?? null });
      return;
    }
    if (this.run.phase === 'ASK_CPF') {
      const result = validateBotAnswer('CPF', event.text ?? '', { isTextMessage: event.isTextMessage });
      if (!result.ok) {
        this.invalidAnswer(node, result.reason, texts.askCpf ?? BOT_DEFAULT_TEXTS.askCpf);
        return;
      }
      // o CPF fica só até a próxima resposta, para a conferência; nunca vai para as variáveis nem para os passos
      this.run.pendingWeak = { cpf: String(result.value) };
      this.rememberAnswer(node, 'CPF', true);
      this.sendText(this.render(texts.askBirthDate ?? BOT_DEFAULT_TEXTS.askBirthDate));
      this.run.attempts = 0;
      this.waitForAnswer(node, { phase: 'ASK_BIRTH' });
      return;
    }
    if (this.run.phase === 'ASK_BIRTH') {
      const result = validateBotAnswer('DATE', event.text ?? '', { pastOnly: true, today: localDateOf(this.now), isTextMessage: event.isTextMessage });
      if (!result.ok) {
        this.invalidAnswer(node, result.reason, texts.askBirthDate ?? BOT_DEFAULT_TEXTS.askBirthDate);
        return;
      }
      const cpf = this.run.pendingWeak?.cpf ?? '';
      this.run.pendingWeak = null;
      this.rememberAnswer(node, 'data de nascimento', true);
      this.callPort('serviceDesk', 'identifyByWeakData', { cpf, studentBirthDate: String(result.value) }, 'IDENTIFY_WEAK');
      return;
    }
    this.invalidAnswer(node, 'OPTION_NOT_FOUND');
  }

  /** @param {BotNode} node @param {string[]} studentPersonIds @param {boolean} isWholeFamily */
  chooseStudents(node, studentPersonIds, isWholeFamily) {
    const offered = this.run.offered ?? [];
    this.run.students = studentPersonIds.map((personId) => {
      const option = offered.find((item) => item.id === personId);
      return { personId, firstName: option?.value ?? option?.title ?? null, gradeLevelLabel: option?.description ?? null };
    });
    this.callPort('serviceDesk', 'chooseStudents', { studentPersonIds, isWholeFamily }, 'CHOOSE_STUDENTS', { branch: isWholeFamily ? 'wholeFamily' : 'chosen' });
  }

  /** @param {BotNode} node */
  verificationRetry(node) {
    const verification = this.run.verification ?? { codeId: null, destination: null, attempts: 0 };
    verification.attempts += 1;
    this.run.verification = verification;
    this.log(node, 'INVALID', null, { reason: 'NO_MATCH' });
    if (verification.attempts >= VERIFICATION_MAXIMUM_ATTEMPTS) {
      this.log(node, 'PASSED', 'failed');
      this.proceed(node, 'failed');
      return;
    }
    this.sendText(this.render(node.config?.texts?.invalid ?? BOT_DEFAULT_TEXTS.verificationInvalid));
    this.waitForAnswer(node, { phase: 'CODE' });
  }

  // ---------------------------------------------------------------------------------------------------------------
  // resultados das portas e da integração

  /** @param {any} result */
  resumeEffect(result) {
    const pending = /** @type {NonNullable<BotRunState['pendingEffect']>} */ (this.run.pendingEffect);
    this.run.pendingEffect = null;
    this.run.waitingFor = null;
    const node = pending.nodeId ? this.nodeOf(pending.nodeId) : null;
    if (pending.nodeId) this.run.currentNodeId = pending.nodeId;
    if (result?.error) {
      if (node) this.log(node, 'FAILED', null, { reason: typeof result.error?.code === 'string' && result.error.code === 'PERMISSION_DENIED' ? 'PERMISSION_DENIED' : 'ERROR' });
      // a transferência que falhou não tenta transferir de novo
      if (pending.purpose === 'HANDOFF_SERVICE' || pending.purpose === 'HANDOFF_CAPTURE') this.finish('FAILED', 'ERROR');
      else this.startHandoff(this.humanTarget(), null, 'ERROR');
      return;
    }
    switch (pending.purpose) {
      case 'IDENTIFY_PHONE': return this.afterIdentifyByPhone(/** @type {BotNode} */ (node), result);
      case 'CONFIRM_SPEAKER':
        if (result?.status === 'BLOCKED') return this.takeBranch(/** @type {BotNode} */ (node), 'blocked');
        return this.identified(/** @type {BotNode} */ (node), String(pending.data?.personId ?? ''), pending.data?.firstName ?? null, 'PHONE_MATCH');
      case 'IDENTIFY_WEAK': return this.afterIdentifyWeak(/** @type {BotNode} */ (node), result);
      case 'LIST_STUDENTS': return this.afterListStudents(/** @type {BotNode} */ (node), result);
      case 'CHOOSE_STUDENTS': return this.takeBranch(/** @type {BotNode} */ (node), pending.data?.branch ?? 'chosen');
      case 'REQUEST_CODE': return this.afterRequestCode(/** @type {BotNode} */ (node), result);
      case 'VERIFY_CODE': return this.afterVerifyCode(/** @type {BotNode} */ (node), result);
      case 'SET_PURPOSE':
        this.run.threadPurpose = node?.config?.purpose ?? this.run.threadPurpose;
        return this.takeBranch(/** @type {BotNode} */ (node), null);
      case 'OPEN_TICKET': {
        this.run.ticket = { id: result?.ticketId ?? null, protocol: result?.protocol ?? null, subject: node ? this.render(node.config?.subjectTemplate) : null };
        const teamId = pending.data?.teamId ?? null;
        if (teamId) this.run.team = { id: teamId, name: this.context.teamsById?.[teamId]?.name ?? null, nextOpeningAt: this.context.teamOpen?.[teamId]?.nextOpeningAt ?? null };
        if (this.threadPurpose === 'PENDING') this.run.threadPurpose = 'SERVICE';
        if (result?.created !== false) this.counters.ticketsOpened += 1;
        return this.takeBranch(/** @type {BotNode} */ (node), null);
      }
      case 'OPEN_RETENTION':
        if (result?.created) this.counters.ticketsOpened += 1;
        return this.takeBranch(/** @type {BotNode} */ (node), null);
      case 'CREATE_CARD':
      case 'VISIT_CARD': {
        if (result?.cardId) this.run.cardId = String(result.cardId);
        if (result?.created) this.counters.cardsCreated += 1;
        if (this.threadPurpose === 'PENDING') this.run.threadPurpose = 'CAPTURE';
        if (pending.purpose === 'VISIT_CARD') {
          if (!node) return this.finish('FAILED', 'ERROR');
          this.listVisitSlots(node, this.now);
          return undefined;
        }
        return this.takeBranch(/** @type {BotNode} */ (node), null, { fieldsWritten: result?.fieldsWritten ?? [] });
      }
      case 'CRM_ACTION': {
        if (!node) return this.finish('FAILED', 'ERROR');
        const status = result?.status === 'SKIPPED' ? 'SKIPPED' : result?.status === 'FAILED' ? 'FAILED' : 'PASSED';
        this.log(node, status, null, { reason: result?.reason ?? null, fieldsWritten: result?.fieldsWritten ?? [] });
        return this.proceed(node, null);
      }
      case 'CONDITION_FIELDS': {
        if (!node) return this.finish('FAILED', 'ERROR');
        const branch = this.evaluateRules(node, Array.isArray(result?.values) ? result.values : []) ? 'yes' : 'no';
        return this.takeBranch(node, branch);
      }
      case 'WRITE_FIELD': {
        if (!node) return this.finish('FAILED', 'ERROR');
        const branch = pending.data?.branch ?? 'answered';
        const status = result?.status === 'SKIPPED' ? 'SKIPPED' : 'PASSED';
        this.log(node, status, branch, { reason: result?.reason ?? null, fieldsWritten: result?.fieldsWritten ?? [] });
        return this.proceed(node, branch);
      }
      case 'DYNAMIC_OPTIONS': return node ? this.offerDynamicOptions(node, result?.options ?? []) : this.finish('FAILED', 'ERROR');
      case 'VISIT_SLOTS': return this.afterVisitSlots(/** @type {BotNode} */ (node), result);
      case 'VISIT_BOOK': return this.afterVisitBook(/** @type {BotNode} */ (node), result, pending.data);
      case 'HANDOFF_SERVICE': return this.afterServiceHandoff(pending.data, result);
      case 'HANDOFF_CAPTURE': {
        const data = pending.data ?? {};
        const handoffNode = data.nodeId ? this.nodeOf(data.nodeId) : null;
        const text = data.origin === 'LEGAL' ? BOT_DEFAULT_TEXTS.legalRestriction : this.render(handoffNode?.config?.openText ?? BOT_DEFAULT_TEXTS.handoffCapture);
        if (data.origin !== 'ERROR') this.sendText(text);
        if (this.threadPurpose === 'PENDING') this.run.threadPurpose = 'CAPTURE';
        return this.finishHandoff(data);
      }
      case 'EXTERNAL': return this.afterIntegration(/** @type {BotNode} */ (node), result);
      default:
        return this.finish('FAILED', 'ERROR');
    }
  }

  /** @param {BotNode} node @param {string | null} branch @param {{ fieldsWritten?: string[] }} [extra] */
  takeBranch(node, branch, extra = {}) {
    if (!node) {
      this.finish('FAILED', 'ERROR');
      return;
    }
    this.log(node, 'PASSED', branch, extra);
    this.proceed(node, branch);
  }

  /** @param {BotNode} node @param {string} personId @param {string | null} firstName @param {string} level */
  identified(node, personId, firstName, level) {
    this.run.speaker = { personId, firstName, name: firstName };
    if ((IDENTITY_RANK[level] ?? 0) > (IDENTITY_RANK[this.identityLevel] ?? 0)) this.run.identityLevel = level;
    this.takeBranch(node, 'identified');
  }

  /** @param {BotNode} node @param {any} result */
  afterIdentifyByPhone(node, result) {
    const config = node.config ?? {};
    const status = result?.status;
    if (status === 'IDENTIFIED') {
      const candidate = (result.candidates ?? []).find((/** @type {any} */ item) => item.personId === result.personId) ?? result.candidates?.[0] ?? null;
      this.identified(node, String(result.personId ?? candidate?.personId ?? ''), candidate?.firstName ?? null, 'PHONE_MATCH');
      return;
    }
    if (status === 'BLOCKED') {
      this.takeBranch(node, 'blocked');
      return;
    }
    if (status === 'SHARED' && config.askSpeakerWhenShared !== false && (result.candidates ?? []).length > 0) {
      /** @type {OfferedOption[]} */
      const offered = result.candidates.slice(0, 10).map((/** @type {any} */ item) => ({ id: String(item.personId), title: String(item.firstName ?? '') }));
      this.sendOptions(node, this.render(config.texts?.askWho ?? BOT_DEFAULT_TEXTS.askWho), offered);
      this.waitForAnswer(node, { phase: 'ASK_WHO', offered });
      return;
    }
    if (config.allowWeakData !== false) {
      this.sendText(this.render(config.texts?.askCpf ?? BOT_DEFAULT_TEXTS.askCpf));
      this.waitForAnswer(node, { phase: 'ASK_CPF', offered: [] });
      return;
    }
    this.notFound(node);
  }

  /** @param {BotNode} node */
  notFound(node) {
    const text = node.config?.texts?.notFound;
    if (text) this.sendText(this.render(text));
    this.takeBranch(node, 'notFound');
  }

  /** @param {BotNode} node @param {any} result */
  afterIdentifyWeak(node, result) {
    if (result?.status === 'IDENTIFIED') {
      this.identified(node, String(result.personId ?? ''), result.firstName ?? null, 'WEAK_DATA');
      return;
    }
    if (result?.status === 'BLOCKED') {
      this.takeBranch(node, 'blocked');
      return;
    }
    this.run.weakFailures = (this.run.weakFailures ?? 0) + 1;
    // a resposta nunca diz qual dos dois dados falhou; o segundo erro segue por notFound (seção 4.8)
    if (result?.status === 'TOO_MANY_ATTEMPTS' || this.run.weakFailures >= 2) {
      this.notFound(node);
      return;
    }
    this.log(node, 'INVALID', null, { reason: 'NO_MATCH' });
    this.sendText(this.render(this.settings.invalidText ?? ''));
    this.sendText(this.render(node.config?.texts?.askCpf ?? BOT_DEFAULT_TEXTS.askCpf));
    this.run.attempts = 0;
    this.waitForAnswer(node, { phase: 'ASK_CPF', offered: [] });
  }

  /** @param {BotNode} node @param {any} result */
  afterListStudents(node, result) {
    const config = node.config ?? {};
    const topic = config.topic ?? 'GENERAL';
    const students = (Array.isArray(result) ? result : result?.students ?? []).filter((/** @type {any} */ student) => {
      const decision = typeof student.disclosure === 'string' ? student.disclosure : student.disclosure?.[topic];
      return decision !== 'DENIED' && decision !== 'BLOCKED';
    });
    if (students.length === 0) {
      this.takeBranch(node, 'none');
      return;
    }
    /** @type {OfferedOption[]} */
    const offered = students.slice(0, 10).map((/** @type {any} */ student) => ({
      id: String(student.personId), title: [student.firstName, student.gradeLevelLabel].filter(Boolean).join(' - '), value: student.firstName ?? null,
      description: student.gradeLevelLabel ?? null,
    }));
    if (offered.length === 1) {
      this.run.offered = offered;
      this.chooseStudents(node, [offered[0].id], false);
      return;
    }
    if (config.allowWholeFamily !== false && offered.length < 10) offered.push({ id: WHOLE_FAMILY_OPTION_ID, title: BOT_DEFAULT_TEXTS.wholeFamilyOption });
    this.sendOptions(node, this.render(config.text ?? BOT_DEFAULT_TEXTS.chooseStudent), offered);
    this.waitForAnswer(node, { offered });
  }

  /** @param {BotNode} node @param {any} result */
  afterRequestCode(node, result) {
    if (result?.status === 'SENT') {
      this.run.verification = { codeId: result.codeId ?? null, destination: result.destinationMask ?? null, attempts: 0 };
      this.sendText(this.render(node.config?.texts?.sent ?? node.config?.texts?.askCode ?? BOT_DEFAULT_TEXTS.verificationSent));
      this.waitForAnswer(node, { phase: 'CODE', offered: [] });
      return;
    }
    this.takeBranch(node, result?.status === 'TOO_MANY_ATTEMPTS' ? 'failed' : 'unavailable');
  }

  /** @param {BotNode} node @param {any} result */
  afterVerifyCode(node, result) {
    if (result?.status === 'VERIFIED') {
      this.run.identityLevel = 'STRONG_CODE';
      this.takeBranch(node, 'verified');
      return;
    }
    if (result?.status === 'INVALID' && Number(result.attemptsLeft ?? 0) > 0) {
      this.verificationRetry(node);
      return;
    }
    this.takeBranch(node, 'failed');
  }

  /** Respostas da conversa para a página de agendamento: as variáveis do bot, sem as sensíveis. @returns {Record<string, string>} */
  bookingAnswers() {
    const sensitive = new Set(this.run.sensitiveVariables ?? []);
    return Object.fromEntries(Object.entries(this.run.variables ?? {}).filter(([name]) => !sensitive.has(name)).map(([name, value]) => [name, String(value ?? '')]));
  }

  /** @param {BotNode} node @param {any} result */
  afterVisitSlots(node, result) {
    const slots = (Array.isArray(result?.slots) ? result.slots : []).filter((/** @type {any} */ slot) => slot && !Number.isNaN(Date.parse(String(slot.startsAt))));
    if (slots.length === 0) {
      this.takeBranch(node, 'noSlots');
      return;
    }
    const timeZone = typeof result?.timeZone === 'string' && result.timeZone ? result.timeZone : TIME_ZONE;
    this.run.visitTimeZone = timeZone;
    const hasMore = slots.length > MAXIMUM_VISIT_ROWS;
    const shown = hasMore ? slots.slice(0, MAXIMUM_VISIT_ROWS - 1) : slots;
    // a próxima página começa no primeiro horário que não coube, para nenhum se perder
    this.run.visitNextFrom = hasMore ? String(slots[MAXIMUM_VISIT_ROWS - 1].startsAt) : null;
    /** @type {OfferedOption[]} */
    const offered = shown.map((/** @type {any} */ slot, /** @type {number} */ index) => ({ id: `s${index + 1}`, title: formatSlotTitle(slot.startsAt, timeZone), value: String(slot.startsAt) }));
    if (hasMore) offered.push({ id: MORE_SLOTS_OPTION_ID, title: BOT_DEFAULT_TEXTS.moreSlots });
    this.sendOptions(node, this.render(node.config?.text ?? BOT_DEFAULT_TEXTS.scheduleVisit), offered, { presentation: 'LIST' });
    this.waitForAnswer(node, { offered });
  }

  /** @param {BotNode} node @param {any} result @param {any} data */
  afterVisitBook(node, result, data) {
    if (result?.status === 'CONFIRMED') {
      this.run.visitDateTime = result.startsAt ?? data?.startsAt ?? null;
      this.run.visitNextFrom = null;
      this.sendText(this.render(node.config?.confirmText ?? BOT_DEFAULT_TEXTS.scheduleVisitConfirm));
      this.takeBranch(node, 'booked');
      return;
    }
    if (result?.status === 'SLOT_TAKEN') {
      this.run.slotTakenCount = (this.run.slotTakenCount ?? 0) + 1;
      if (this.run.slotTakenCount <= MAXIMUM_SLOT_TAKEN_RETRIES) {
        this.sendText(BOT_DEFAULT_TEXTS.slotTaken);
        this.listVisitSlots(node, this.now);
        return;
      }
      this.takeBranch(node, 'slotTaken');
      return;
    }
    // NOT_AVAILABLE: a página saiu do ar ou o agendamento integrado não está ligado
    this.takeBranch(node, 'noSlots');
  }

  /** @param {any} data @param {any} result */
  afterServiceHandoff(data, result) {
    const handoffNode = data?.nodeId ? this.nodeOf(data.nodeId) : null;
    const teamId = result?.teamId ?? data?.target?.teamId ?? this.context.queueTeamId ?? null;
    this.run.ticket = { id: result?.ticketId ?? null, protocol: result?.protocol ?? null, subject: this.run.ticket?.subject ?? null };
    this.run.team = { id: teamId, name: this.context.teamsById?.[teamId]?.name ?? result?.teamName ?? null, nextOpeningAt: result?.nextOpeningAt ?? null };
    this.run.threadPurpose = 'SERVICE';
    if (data?.origin === 'LEGAL') this.sendText(BOT_DEFAULT_TEXTS.legalRestriction);
    else if (data?.origin !== 'ERROR') {
      const isTeamOpen = result?.isTeamOpen === true;
      const hasAgent = Number(result?.availableAgents ?? 0) > 0;
      // no horário, sem ninguém disponível, o padrão não fala em "volta às": a próxima abertura seria agora
      const text = isTeamOpen && hasAgent
        ? handoffNode?.config?.openText ?? BOT_DEFAULT_TEXTS.handoffOpen
        : handoffNode?.config?.closedText ?? (isTeamOpen ? BOT_DEFAULT_TEXTS.handoffBusy : BOT_DEFAULT_TEXTS.handoffClosed);
      this.sendText(this.render(text));
    }
    if (result?.created) this.counters.ticketsOpened += 1;
    this.finishHandoff(data ?? { origin: 'NODE', nodeId: null });
  }

  /** @param {BotNode} node @param {any} result */
  afterIntegration(node, result) {
    if (!node) {
      this.finish('FAILED', 'ERROR');
      return;
    }
    if (result?.ok) {
      const body = limitBody(result.body);
      this.run.stepOutputs = { ...this.run.stepOutputs, [node.id]: { status: result.status ?? null, body } };
      const variable = node.config?.saveResponseTo?.variable;
      if (variable) this.setVariable(variable, typeof body === 'string' ? body : JSON.stringify(body ?? null));
      this.takeBranch(node, 'success', { fieldsWritten: variable ? [`bot.${variable}`] : [] });
      return;
    }
    this.log(node, 'FAILED', 'failed', { reason: 'INTEGRATION_FAILED' });
    this.proceed(node, 'failed');
  }
}

/** Resposta guardada até 8 KB (seção 4.18). @param {unknown} body */
function limitBody(body) {
  const text = typeof body === 'string' ? body : JSON.stringify(body ?? null);
  if (new TextEncoder().encode(text).length <= MAXIMUM_BOT_INTEGRATION_RESPONSE_BYTES) return body;
  return text.slice(0, MAXIMUM_BOT_INTEGRATION_RESPONSE_BYTES / 2);
}

/** @param {unknown} value @param {(text: string) => string} render @returns {unknown} */
function renderJsonStrings(value, render) {
  if (typeof value === 'string') return render(value);
  if (Array.isArray(value)) return value.map((item) => renderJsonStrings(item, render));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, renderJsonStrings(item, render)]));
  return value;
}

/** @param {string} now */
function localDateOf(now) {
  const local = localParts(Date.parse(now), TIME_ZONE);
  return `${local.year}-${String(local.month).padStart(2, '0')}-${String(local.day).padStart(2, '0')}`;
}

/**
 * Título curto do horário na lista (até 24 caracteres): "seg 20/10 09:00", no fuso da página de agendamento.
 * @param {string} startsAt @param {string} [timeZone]
 */
export function formatSlotTitle(startsAt, timeZone = TIME_ZONE) {
  const at = Date.parse(startsAt);
  if (Number.isNaN(at)) return String(startsAt);
  const local = localParts(at, timeZone);
  const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');
  return `${SHORT_WEEKDAYS[weekdayInTimeZone(at, timeZone)]} ${pad(local.day)}/${pad(local.month)} ${pad(local.hour)}:${pad(local.minute)}`;
}

/**
 * Regra de VARIABLE ou FIELD (seção 4.3). Texto compara sem acento e sem diferença de maiúsculas.
 * @param {unknown} value @param {string} operator @param {unknown} expected
 */
export function compareValues(value, operator, expected) {
  const isEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
  const text = Array.isArray(value) ? value.map(String).join(' ') : typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? '');
  switch (operator) {
    case 'isEmpty': return isEmpty;
    case 'isNotEmpty': return !isEmpty;
    case 'equals': return Array.isArray(value) ? value.map(normalizeAnswerText).includes(normalizeAnswerText(String(expected ?? ''))) : normalizeAnswerText(text) === normalizeAnswerText(String(expected ?? ''));
    case 'notEquals': return !compareValues(value, 'equals', expected);
    case 'contains': return normalizeAnswerText(text).includes(normalizeAnswerText(String(expected ?? '')));
    case 'in': return Array.isArray(expected) && expected.map((item) => normalizeAnswerText(String(item))).includes(normalizeAnswerText(text));
    case 'greaterThan':
    case 'lessThan': {
      const left = Number(String(value ?? '').replace(',', '.'));
      const right = Number(String(expected ?? '').replace(',', '.'));
      if (Number.isFinite(left) && Number.isFinite(right) && text.trim() !== '') return operator === 'greaterThan' ? left > right : left < right;
      if (isEmpty) return false;
      return operator === 'greaterThan' ? text > String(expected ?? '') : text < String(expected ?? '');
    }
    default: return false;
  }
}

const WEEKDAY_KEYS = Object.freeze(['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']);

/**
 * Horário de São Paulo entre `from` e `to` (HH:MM) nos dias pedidos; `from` depois de `to` passa da meia-noite.
 * @param {string} now @param {{ from?: string, to?: string, weekdays?: string[] }} rule
 */
export function isWithinTimeOfDay(now, rule) {
  const at = Date.parse(now);
  const local = localParts(at, TIME_ZONE);
  const weekdays = Array.isArray(rule.weekdays) && rule.weekdays.length > 0 ? rule.weekdays : WEEKDAY_KEYS;
  if (!weekdays.includes(WEEKDAY_KEYS[weekdayInTimeZone(at, TIME_ZONE)])) return false;
  const minutes = local.hour * 60 + local.minute;
  const toMinutes = (/** @type {unknown} */ value) => {
    const [hour, minute] = String(value ?? '00:00').split(':').map(Number);
    return (hour || 0) * 60 + (minute || 0);
  };
  const from = toMinutes(rule.from);
  const to = toMinutes(rule.to);
  return from <= to ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}

/** Blocos de captação, para a mensagem de pendência na tela e os testes. */
export const CAPTURE_NODE_TYPES = BOT_CAPTURE_ONLY_NODE_TYPES;
export { isWaitingNode };
